import React, { useEffect, useId, useState } from "react";
import { useIntl } from "react-intl";
import typeOf from "kind-of";
import classNames from "classnames";
import {
    DictType, ScenarioDiffItem, ScenarioDiffItemType,
    ScenarioDiffType, DiffSceneData, DiffTemplateData
} from "@/model";
import { isEmpty } from "@/util/typeUtil";
import { useScenarioDeployDiff, useScenarioDeploy } from "@/query/deployment";
import { useScenarioId, useModal } from "@/hook";
import { Modal } from "@/component/modal";
import Tooltip from "@/component/Tooltip";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import { ReactComponent as LoadingIcon } from "@/assets/svgs/loading.svg";


export const DEPLOYMENT_DIFF_MODAL_ID = "deployment-diff-modal-id";

const DeploymentDiffModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();
    const deployId = modal.getData(DEPLOYMENT_DIFF_MODAL_ID);

    const [diffFrom, setDiffFrom] = useState<'scenario' | 'previous'>('scenario');
    const [openType, setOpenType] = useState<ScenarioDiffType>('changed');

    const { data: deployDiff, isLoading: isLoadingDeployDiff } = useScenarioDeployDiff(scenarioId, deployId);
    const { data: deploy, isLoading: isLoadingDeploy } = useScenarioDeploy(scenarioId, deployId);

    const isLoading = diffFrom === 'scenario' ? isLoadingDeployDiff : isLoadingDeploy;
    const diff = diffFrom === 'scenario' ? deployDiff : deploy?.diff;

    const onClose = () => {
        setTimeout(() => {
            setOpenType('changed');
        }, 300);
    };

    useEffect(() => {
        if (!diff) {
            return;
        }

        if (diff.changed) {
            setOpenType('changed');
        } else if (diff.added) {
            setOpenType('added');
        } else if (diff.removed) {
            setOpenType('removed');
        } else {
            setOpenType('changed');
        }
    }, [diff]);

    return (
        <Modal id={DEPLOYMENT_DIFF_MODAL_ID}
               className="modal-lg-upload"
               title={intl.formatMessage({ id: "i06001" })}
               onClose={onClose}
        >
            <div className="mt-4 flex flex-col gap-5">
                <div className="flex items-center gap-5">
                    <button className={classNames(
                                 "py-2 flex items-center gap-1 border-b text-[14px] leading-[1.2] cursor-pointer",
                                 diffFrom === 'scenario'
                                     ? "font-[600] text-chat42 border-chat42"
                                     : "font-[400] text-black-400 border-transparent"
                             )}
                             aria-pressed={diffFrom === 'scenario'}
                             onClick={() => setDiffFrom('scenario')}
                    >
                        {intl.formatMessage({ id: "i06034" })}
                        <Tooltip message={intl.formatMessage({ id: "i06036" })} />
                    </button>
                    <button className={classNames(
                                 "py-2 flex items-center gap-1 border-b text-[14px] leading-[1.2] cursor-pointer",
                                 diffFrom === 'previous'
                                     ? "font-[600] text-chat42 border-chat42"
                                     : "font-[400] text-black-400 border-transparent"
                             )}
                             aria-pressed={diffFrom === 'previous'}
                             onClick={() => setDiffFrom('previous')}
                    >
                        {intl.formatMessage({ id: "i06035" })}
                        <Tooltip message={intl.formatMessage({ id: "i06037" })} />
                    </button>
                </div>

                <div className="flex flex-col gap-1">
                    {!isEmpty(diff) ? (
                        <>
                            <DiffAccordion type="changed"
                                           isOpen={openType === 'changed'}
                                           diff={diff?.changed}
                                           onClickHeader={() => setOpenType('changed')}
                            />
                            <DiffAccordion type="added"
                                           isOpen={openType === 'added'}
                                           diff={diff?.added}
                                           onClickHeader={() => setOpenType('added')}
                            />
                            <DiffAccordion type="removed"
                                           isOpen={openType === 'removed'}
                                           diff={diff?.removed}
                                           onClickHeader={() => setOpenType('removed')}
                            />
                        </>
                    ) : (
                        <div className="h-[420px] flex items-center justify-center">
                            {isLoading
                                ? <div role="status">
                                    <LoadingIcon className="animate-spin size-16"
                                                 aria-hidden="true" />
                                </div>
                                : (
                                    <div className="text-[14px] font-[400] text-black-400">
                                        {intl.formatMessage({ id: diffFrom === 'scenario' ? "i06033" : "i06038" })}
                                    </div>
                                )
                            }
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

const DIFF_CONTENT_TITLES: Record<ScenarioDiffType, Record<ScenarioDiffItemType, string>> = {
    changed: {
        scenes: "i06016",
        templates: "i06022",
        scenario: "i06019",
        publish: "i06025"
    },
    added: {
        scenes: "i06017",
        templates: "i06023",
        scenario: "i06020",
        publish: "i06026"
    },
    removed: {
        scenes: "i06018",
        templates: "i06024",
        scenario: "i06021",
        publish: "i06027"
    }
};

const DIFF_ACCORDION_TITLE: Record<ScenarioDiffType, string> = {
    changed: "i06032",
    added: "b0009",
    removed: "b0006"
};

interface DiffAccordionProps {
    type: ScenarioDiffType;
    isOpen: boolean;
    diff?: ScenarioDiffItem;
    onClickHeader: () => void;
}

const DiffAccordion: React.FC<DiffAccordionProps> = (props) => {
    const { type, isOpen, diff = {}, onClickHeader } = props;
    const intl = useIntl();
    const panelId = useId();

    const titles = DIFF_CONTENT_TITLES[type];
    const isEmpty = !diff.scenes && !diff.templates && !diff.scenario && !diff.publish;

    return (
        <div className={classNames(
                 "border border-black-200 rounded-[6px]",
                 !isOpen && "hover:bg-black-100"
             )}
        >
            <button className="py-3 px-4 w-full flex items-center justify-between"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={onClickHeader}
            >
                {intl.formatMessage({ id: DIFF_ACCORDION_TITLE[type] })}
                <ChevronArrowIcon direction={isOpen ? "up" : "down"}
                                  className="size-4.5 transition-transform duration-200"
                />
            </button>

            <div id={panelId}
                 role="region"
                 aria-hidden={!isOpen}
                 className={classNames(
                     "py-3 px-4 border-t border-black-200",
                     "transition-disclosure",
                     isOpen ? "open" : "closed hidden"
                 )}
            >
                <div className="h-[400px] flex flex-col gap-8 text-[14px] font-[400] text-black-500 overflow-auto ">
                    {diff.scenes && (
                        <div className={classNames(
                                 "flex flex-col gap-2",
                                 !diff.scenes && "hidden"
                             )}
                        >
                            <div className="font-[600]">
                                {`${intl.formatMessage({ id: titles.scenes })}(${Object.keys(diff.scenes).length})`}
                            </div>
                            <DiffParagraphContent itemType="scenes"
                                                  type={type}
                                                  diff={diff.scenes}       
                            />
                        </div>
                    )}
                    {diff.templates && (
                        <div className={classNames(
                                 "flex flex-col gap-2",
                                 !diff.templates && "hidden"
                             )}
                        >
                            <div className="font-[600]">
                                {`${intl.formatMessage({ id: titles.templates })}(${Object.keys(diff.templates).length})`}
                            </div>
                            <DiffParagraphContent itemType="templates"
                                                  type={type}
                                                  diff={diff.templates}       
                            />
                        </div>
                    )}
                    {diff.scenario && (
                        <div className={classNames(
                                 "flex flex-col gap-2",
                                 !diff.scenario && "hidden"
                             )}
                        >
                            <div className="font-[600]">
                                {`${intl.formatMessage({ id: titles.scenario })}(${Object.keys(diff.scenario.data).length})`}
                            </div>
                            <div className="ml-2.5 flex flex-col gap-2">
                                {Object.entries(diff.scenario)
                                    .map(([key, value]) => (
                                        <div key={key}>
                                            {renderDiff(value, type === "changed")}
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}
                    {diff.publish && (
                        <div className={classNames(
                                 "flex flex-col gap-2",
                                 !diff.publish && "hidden"
                             )}
                        >
                            <div className="font-[600]">
                                {`${intl.formatMessage({ id: titles.publish })}(${Object.keys(diff.publish.data).length})`}
                            </div>
                            <div className="ml-2.5 flex flex-col gap-2">
                                {Object.entries(diff.publish)
                                    .map(([key, value]) => (
                                        <div key={key}>
                                            {renderDiff(value, type === "changed")}
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )}
                    {isEmpty && (
                        <div className="size-full flex items-center justify-center text-[14px] font-[400] text-black-400">
                            {intl.formatMessage({ id: "i06033" })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

interface DiffParagraphContentProps {
    type: ScenarioDiffType;
    itemType: "scenes" | "templates";
    diff: DictType<DiffSceneData> | DictType<DiffTemplateData>;
}

const DiffParagraphContent: React.FC<DiffParagraphContentProps> = ({ diff, type, itemType }) => {
    return (
        <div className="ml-2.5 flex flex-col gap-5">
            {Object.entries(diff).map(([id, item]) => (
                <div key={id} className="flex flex-col gap-1">
                    <div className="font-[600]">
                        {itemType === "scenes"
                            ? `#${(item as DiffSceneData).custom_id}(${id}): ${item.name}`
                            : `#${(item as DiffTemplateData).name}(${id})`}
                    </div>
                    <div className="ml-2.5 flex flex-col gap-0.5">
                        {item.data && Object.entries(item.data).map(([key, value]) => (
                            <div key={key} className="flex gap-1">
                                <div className="font-[600]">
                                    {`${key}:`}
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    {renderDiff(value, type === "changed")}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const toString = (value: any) => (
    typeof value === "object" ? JSON.stringify(value) : String(value)
);

const renderDiff = (data: any, isChangeSection: boolean) => {
    if (isChangeSection && typeOf(data) === "object" && 'new_value' in data && 'old_value' in data) {
        return (
            <div className="ml-1 flex gap-1">
                <div className="line-through">
                    {toString(data.old_value)}
                </div>
                <div aria-hidden="true">▶</div>
                <div>
                    {toString(data.new_value)}
                </div>
            </div>
        );
    }

    if (Array.isArray(data)) {
        return data.map((value, index) => (
            <div key={index}>
                {renderDiff(value, isChangeSection)}
            </div>
        ));
    }

    if (typeof data === "object") {
        if (typeOf(data) === "date") {
            data = data.toJSON();
        }

        return Object.entries(data).map(([key, value], index) => (
            <div key={`${key}-${index}`} className="flex gap-1">
                <div className="font-[600]">
                    {`${key}:`}
                </div>
                <div className="flex flex-col gap-0.5">
                    {renderDiff(value, isChangeSection)}
                </div>
            </div>
        ));
    }

    return (
        <div>
            {toString(data)}
        </div>
    );
};

export default DeploymentDiffModal;
